import { useState, useCallback } from 'react';
import {
  getFunctionInformation,
  getTasksFromViewPlanning,
  getView,
  updateViewPlanning,
} from 'services/api';
import { PlanningForm, Task } from 'types/types';
import { Mode } from '../types';
import {
  buildTimelineMatrixFromTasks,
  formatDate,
  getDatesBetweenDates,
} from '../PlanningVue.utils';

const colorsArrayElectrovanne = ['#4789BC', '#A6E5EB'];
const colorsArrayBaseVie = ['#FF5722', '#FFBF00', '#4CAF50', '#2196F3'];

interface UsePlanningDataProps {
  viewId?: string | number;
  projectId?: string | number;
}

export const usePlanningData = ({
  viewId = undefined,
  projectId,
}: UsePlanningDataProps) => {
  const [defaultModes, setDefaultModes] = useState<Mode[]>([]);
  const [selectedMode, setSelectedMode] = useState<Mode>();
  const [markerMatrix, setMarkerMatrix] = useState<number[][]>(
    Array.from({ length: 7 }, () => [])
  );
  const [timelineMatrix, setTimelineMatrix] = useState<Mode[][]>(
    Array.from({ length: 7 }, () =>
      Array.from({ length: 48 }, () => defaultModes[0])
    )
  );
  const [tasks, setTasks] = useState<Task[]>([]);
  const [defaultTask, setDefaultTask] = useState<Task>({} as Task);
  const [actualView, setActualView] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [savingState, setSavingState] = useState<number>(0);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const effectiveViewId =
        viewId !== undefined ? parseInt(String(viewId), 10) : NaN;
      if (Number.isNaN(effectiveViewId)) {
        setLoading(false);
        return;
      }

      const response = await getView({
        view_id: effectiveViewId,
      });
      const view = response.data.view;
      setActualView(view);

      const getFunctionArguments = await getFunctionInformation({
        viewType: view.view_type,
      });
      const functionInfo = getFunctionArguments.data[0];

      if (functionInfo) {
        const modes = functionInfo.arguments_definition[0].options.map(
          (option: { key: string; value: string }, index: number) => ({
            id: option.value,
            name: option.key,
            color:
              view.view_type === 5
                ? colorsArrayElectrovanne[index]
                : colorsArrayBaseVie[index],
          })
        );
        setDefaultModes(modes);
        setSelectedMode(modes[0]);

        const tasksResponse = await getTasksFromViewPlanning({
          viewId: effectiveViewId,
        });
        const allTasks = tasksResponse.data.tasks;
        setTasks(allTasks);
        setDefaultTask(
          allTasks.find((task: any) => task.state === 1 || task.state === 0)
        );

        const { newMarkerMatrix, newTimelineMatrix } =
          buildTimelineMatrixFromTasks(
            allTasks,
            modes,
            colorsArrayElectrovanne,
            colorsArrayBaseVie,
            view.view_type
          );

        setMarkerMatrix(newMarkerMatrix);
        setTimelineMatrix(newTimelineMatrix);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.error('Error during fetchTasks:', e);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [viewId, projectId]);

  const markerToTask = (
    plannedTasks: Task[],
    markerMatrixData: number[][],
    isException: boolean,
    exceptionPlanningInfo: any,
    exTimelineMatrix: Mode[][] | undefined
  ) => {
    const payload: PlanningForm = {
      project_id: projectId
        ? projectId
        : actualView
          ? actualView.project_id
          : -1,
      tasks: [],
      viewId: defaultTask.view_id,
    };

    markerMatrixData.forEach((markers: number[], dayIndex: number) => {
      let dateOfTask;
      if (isException) {
        dateOfTask = new Date(
          getDatesBetweenDates(
            exceptionPlanningInfo.startDate,
            exceptionPlanningInfo.endDate
          )[dayIndex]
        );
      }

      if (!markers.includes(0)) {
        const task0 = {
          ...defaultTask,
          name: isException ? exceptionPlanningInfo.title : defaultTask.name,
          state: isException ? 3 : 1,
          cron_day_of_week: isException ? '*' : JSON.stringify(dayIndex),
          cron_day: isException ? dateOfTask!.getDate() + '' : '*',
          cron_month: isException ? dateOfTask!.getMonth() + 1 + '' : '*',
          cron_year: isException ? dateOfTask!.getFullYear() + '' : '*',
          cron_start_day: isException
            ? formatDate(exceptionPlanningInfo.startDate)
            : new Date().toISOString().split('T')[0],
          cron_end_date: isException
            ? formatDate(exceptionPlanningInfo.endDate)
            : new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                .toISOString()
                .split('T')[0],
          id_arguments: [
            {
              Position_in_arg: 1,
              Value: isException
                ? exTimelineMatrix![dayIndex][0].id
                : timelineMatrix[dayIndex][0].id,
            },
            {
              Position_in_arg: 2,
              Value: isException
                ? plannedTasks[0].sensors_list.split(',')
                : plannedTasks[0].id_arguments![1].Value,
            },
          ],
        };
        payload.tasks.push(task0);
      }

      markers.forEach((marker) => {
        const currentTask = {
          ...defaultTask,
          name: isException ? exceptionPlanningInfo.title : defaultTask.name,
          cron_day_of_week: isException ? '*' : JSON.stringify(dayIndex),
          cron_hour: JSON.stringify(Math.trunc(marker / 2)),
          cron_minute: marker % 2 === 0 ? '0' : '30',
          cron_day: isException ? dateOfTask!.getDate() + '' : '*',
          cron_month: isException ? dateOfTask!.getMonth() + 1 + '' : '*',
          cron_year: isException ? dateOfTask!.getFullYear() + '' : '*',
          cron_start_day: isException
            ? formatDate(exceptionPlanningInfo.startDate)
            : new Date().toISOString().split('T')[0],
          cron_end_date: isException
            ? formatDate(exceptionPlanningInfo.endDate)
            : new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                .toISOString()
                .split('T')[0],
          state: isException ? 3 : 1,
          id_arguments: [
            {
              Position_in_arg: 1,
              Value: isException
                ? exTimelineMatrix![dayIndex][marker].id
                : timelineMatrix[dayIndex][marker].id,
            },
            {
              Position_in_arg: 2,
              Value: isException
                ? plannedTasks[0].sensors_list.split(',')
                : plannedTasks[0].id_arguments![1].Value,
            },
          ],
        };
        payload.tasks.push(currentTask);
      });
    });

    return payload.tasks;
  };

  const handleSave = async (
    allTasks: Task[],
    currentMarkerMatrix: number[][],
    currentTimelineMatrix: Mode[][],
    exMarkerMatrixData: number[][] | undefined,
    exceptionPlanningInfo: any,
    exTimelineMatrixData: Mode[][] | undefined,
    setSnackMessage: (msg: string) => void,
    setOpenSnack: (open: boolean) => void,
    setExceptionPlanningInfo: (data: any) => void
  ) => {
    setSavingState(1);

    const manualTasks = allTasks.filter((task) => task.state === 2);
    const plannedTasks = allTasks.filter(
      (task) => task.state === 0 || task.state === 1
    );
    const exceptionTasks = allTasks.filter(
      (task) =>
        task.state === 3 &&
        task.cron_end_date! !==
          new Intl.DateTimeFormat('fr').format(exceptionPlanningInfo.endDate) &&
        task.cron_start_day! !==
          new Intl.DateTimeFormat('fr').format(exceptionPlanningInfo.startDate)
    );

    exceptionTasks.forEach((task) => {
      task.id_arguments![1].Value = task.sensors_list.split(',');
    });

    const payload: PlanningForm = {
      project_id: projectId
        ? projectId
        : actualView
          ? actualView.project_id
          : -1,
      tasks: [],
      viewId: defaultTask.view_id,
    };

    const tasksPlanned = markerToTask(
      plannedTasks,
      currentMarkerMatrix,
      false,
      exceptionPlanningInfo,
      exTimelineMatrixData
    );
    if (exMarkerMatrixData) {
      const taskException = markerToTask(
        plannedTasks,
        exMarkerMatrixData,
        true,
        exceptionPlanningInfo,
        exTimelineMatrixData
      );
      payload.tasks.push(...taskException);
    }
    payload.tasks.push(...tasksPlanned, ...manualTasks, ...exceptionTasks);

    const requestTime = new Date();
    try {
      const response = await updateViewPlanning(payload);
      await fetchTasks();
      setSnackMessage('Le planning a été mis à jour avec succès.');
      setOpenSnack(true);
      setSavingState(2);
    } catch (error: any) {
      setSavingState(0);
      setSnackMessage('Erreur lors de la mise à jour du planning.');
      setOpenSnack(true);
      console.error('Error updating planning:', error);
    } finally {
      setExceptionPlanningInfo({
        title: '',
        startDate: new Date(),
        endDate: new Date(),
      });
    }
  };

  return {
    defaultModes,
    setDefaultModes,
    selectedMode,
    setSelectedMode,
    markerMatrix,
    setMarkerMatrix,
    timelineMatrix,
    setTimelineMatrix,
    tasks,
    setTasks,
    defaultTask,
    setDefaultTask,
    actualView,
    loading,
    savingState,
    setSavingState,
    fetchTasks,
    handleSave,
  };
};
